import {
    getDashboardStage,
    getDashboardEnvironmentMode,
    isProductionStage,
    isDashboardLocalDebugMode,
    resolveDashboardServiceHost,
    isLocalLikeHost
} from './utils/environmentMode';

const NON_PRODUCTION_DEFAULTS = {
    inventoryHost: 'https://dev-api-inventory.gophr.app',
    inventoryHasuraHost: 'https://dev-api-inventory.hasura.gophr.app/v1/graphql',
    shipmentHost: 'https://dev-api-shipments.gophr.app',
    shipmentHasuraHost: 'https://dev-api-shipments.hasura.gophr.app/v1/graphql',
    shipmentWssHasuraHost: 'wss://dev-api-shipments.hasura.gophr.app/v1/graphql',
    usersHost: 'https://dev-api-users.gophr.app',
    usersHasuraHost: 'https://dev-api-users.hasura.gophr.app/v1/graphql',
    usersHasuraRest: 'https://dev-api-users.hasura.app/api/rest',
    usersWssHasuraHost: 'wss://dev-api-users.hasura.gophr.app/v1/graphql',
    aiHost: 'https://dev-api-ai.gophr.app',
    paymentsHost: 'https://dev-api-payments.gophr.app',
    filesHost: 'https://dev-api-files.gophr.app',
    webTrackingHost: 'https://gophr-tracking-dev.web.app'
};

const PRODUCTION_DEFAULTS = {
    inventoryHost: 'https://api-inventory.gophr.app',
    inventoryHasuraHost: 'https://api-inventory.hasura.gophr.app/v1/graphql',
    shipmentHost: 'https://api-shipments.gophr.app',
    shipmentHasuraHost: 'https://api-shipments.hasura.gophr.app/v1/graphql',
    shipmentWssHasuraHost: 'wss://api-shipments.hasura.gophr.app/v1/graphql',
    usersHost: 'https://api-users.gophr.app',
    usersHasuraHost: 'https://api-users.hasura.gophr.app/v1/graphql',
    usersHasuraRest: 'https://api-users.hasura.gophr.app/api/rest',
    usersWssHasuraHost: 'wss://api-users.hasura.gophr.app/v1/graphql',
    aiHost: 'https://api-ai.gophr.app',
    paymentsHost: 'https://api-payments.gophr.app',
    filesHost: 'https://api-files.gophr.app',
    webTrackingHost: 'https://gophr-tracking-prod.web.app'
};

const resolveNonProductionHost = (name, developmentHost, productionHost) =>
    resolveDashboardServiceHost({
        configuredHost: process.env[name],
        developmentHost,
        productionHost
    });

const shouldUseProductionHostedDefaultsInLocalDebug =
    getDashboardEnvironmentMode() === 'local-debug' && process.env.REACT_APP_LOCAL_DEBUG_HOSTED_STAGE === 'production';

const resolveLocalDebugHostedHost = (developmentHost, productionHost) =>
    shouldUseProductionHostedDefaultsInLocalDebug ? productionHost : developmentHost;

const LOCAL_DEBUG_AUTH_CONFIG = {
    domain: 'gophr.auth0.com',
    clientId: '08DTSFzoagUGkvFibKF8h1Bo8tuXJzBA',
    callbackUrl: 'http://localhost:3000/callback',
    container: 'auth0-login-container',
    audience: 'https://gophr.app',
    jwksUri: 'https://gophr.auth0.com/.well-known/jwks.json',
    algorithms: ['RS256'],
    issuer: 'https://gophr.auth0.com/',
    client: null,
    SIGNING_KEY: 'MzRFOUM3OTE0NzQ5NUI5NjE4OUI0MkUxMkI2RkFFNkYyRUFFNjNENw'
};

const NON_PRODUCTION_AUTH_CONFIG = {
    domain: 'gophr-dev.auth0.com',
    clientId: 'R8Lux0ToGL7R7Jky66Ue1mshz2ZOEDzc',
    callbackUrl: 'http://localhost:3000/callback',
    container: 'auth0-login-container',
    audience: 'https://dev.gophr.app',
    jwksUri: 'https://gophr-dev.auth0.com/.well-known/jwks.json',
    algorithms: ['RS256'],
    issuer: 'https://gophr-dev.auth0.com/',
    client: null,
    SIGNING_KEY: 'NDVFQjY3MUUyMUQ5RkI0RkFFRjAzRjIzNjlEMzUzOTg0MDU5ODUyNw'
};

export const resolveStripePublicKey = () => {
    const configuredKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY || null;

    if (!configuredKey) {
        return null;
    }

    const testKeyOverride = process.env.REACT_APP_STRIPE_TEST_PUBLIC_KEY || process.env.REACT_APP_STRIPE_PUBLIC_KEY_TEST || null;
    const isLiveKey = typeof configuredKey === 'string' && configuredKey.startsWith('pk_live_');
    const isTestKey = typeof configuredKey === 'string' && configuredKey.startsWith('pk_test_');
    const isValidTestOverride = typeof testKeyOverride === 'string' && testKeyOverride.startsWith('pk_test_');
    const browserProtocol = typeof window !== 'undefined' && window.location ? window.location.protocol : '';
    const browserHost = typeof window !== 'undefined' && window.location ? window.location.hostname : '';
    const isLocalHttp = browserProtocol === 'http:' && isLocalLikeHost(browserHost);
    const isHostedHttps = browserProtocol === 'https:' && !isLocalLikeHost(browserHost);

    if (!isProductionStage()) {
        if (isValidTestOverride) {
            return testKeyOverride;
        }

        if (isTestKey) {
            return configuredKey;
        }

        if (isLiveKey && isHostedHttps) {
            return configuredKey;
        }

        if (isLiveKey && isLocalHttp) {
            return null;
        }

        return null;
    }

    return isLiveKey || isTestKey ? configuredKey : null;
};

const dev = {
    environment: {
        mode: getDashboardEnvironmentMode(),
        stage: getDashboardStage()
    },
    api_inventory: {
        admin: {
            inventoryHost: resolveNonProductionHost('REACT_APP_API_INVENTORY_HOST', NON_PRODUCTION_DEFAULTS.inventoryHost, PRODUCTION_DEFAULTS.inventoryHost),
            hasuraHost: resolveNonProductionHost('REACT_APP_API_INVENTORY_HASURA_HOST', resolveLocalDebugHostedHost(NON_PRODUCTION_DEFAULTS.inventoryHasuraHost, PRODUCTION_DEFAULTS.inventoryHasuraHost), PRODUCTION_DEFAULTS.inventoryHasuraHost),
            inventoryHasuraAdminSecret: process.env.REACT_APP_INVENTORY_HASURA_ADMIN_SECRET
        }
    },
    api_shipments: {
        admin: {
            shipmentHost: resolveNonProductionHost('REACT_APP_API_SHIPMENTS_HOST', NON_PRODUCTION_DEFAULTS.shipmentHost, PRODUCTION_DEFAULTS.shipmentHost),
            hasuraHost: resolveNonProductionHost('REACT_APP_API_SHIPMENTS_HASURA_HOST', resolveLocalDebugHostedHost(NON_PRODUCTION_DEFAULTS.shipmentHasuraHost, PRODUCTION_DEFAULTS.shipmentHasuraHost), PRODUCTION_DEFAULTS.shipmentHasuraHost),
            wssHasuraHost: resolveNonProductionHost('REACT_APP_API_SHIPMENTS_WSS_HASURA_HOST', resolveLocalDebugHostedHost(NON_PRODUCTION_DEFAULTS.shipmentWssHasuraHost, PRODUCTION_DEFAULTS.shipmentWssHasuraHost), PRODUCTION_DEFAULTS.shipmentWssHasuraHost),
            shipmentsHasuraAdminSecret: process.env.REACT_APP_SHIPMENTS_HASURA_ADMIN_SECRET
        },

        merchant: {
            shipmentHost: resolveNonProductionHost('REACT_APP_API_SHIPMENTS_HOST', NON_PRODUCTION_DEFAULTS.shipmentHost, PRODUCTION_DEFAULTS.shipmentHost),
            hasuraHost: resolveNonProductionHost('REACT_APP_API_SHIPMENTS_HASURA_HOST', resolveLocalDebugHostedHost(NON_PRODUCTION_DEFAULTS.shipmentHasuraHost, PRODUCTION_DEFAULTS.shipmentHasuraHost), PRODUCTION_DEFAULTS.shipmentHasuraHost)
        }

        // Dev2 shipment endpoints:

        // admin:  {
        //     shipmentHost: "https://dev2-api-shipments.gophr.app",
        //     hasuraHost: "https://dev2-api-shipments.hasura.gophr.app/v1/graphql",
        //     wssHasuraHost: "wss://dev2-api-shipments.hasura.gophr.app/v1/graphql"
        // },

        // merchant: {
        //     shipmentHost: "https://dev2-api-shipments.gophr.app",
        //     hasuraHost: "https://dev2-api-shipments.hasura.gophr.app/v1/graphql"
        // }
    },
    api_users: {
        admin: {
            usersHost: resolveNonProductionHost('REACT_APP_API_USERS_HOST', NON_PRODUCTION_DEFAULTS.usersHost, PRODUCTION_DEFAULTS.usersHost),
            hasuraHost: resolveNonProductionHost('REACT_APP_API_USERS_HASURA_HOST', resolveLocalDebugHostedHost(NON_PRODUCTION_DEFAULTS.usersHasuraHost, PRODUCTION_DEFAULTS.usersHasuraHost), PRODUCTION_DEFAULTS.usersHasuraHost),
            hasuraRest: resolveNonProductionHost('REACT_APP_API_USERS_HASURA_REST', resolveLocalDebugHostedHost(NON_PRODUCTION_DEFAULTS.usersHasuraRest, PRODUCTION_DEFAULTS.usersHasuraRest), PRODUCTION_DEFAULTS.usersHasuraRest),
            wssHasuraHost: resolveNonProductionHost('REACT_APP_API_USERS_WSS_HASURA_HOST', resolveLocalDebugHostedHost(NON_PRODUCTION_DEFAULTS.usersWssHasuraHost, PRODUCTION_DEFAULTS.usersWssHasuraHost), PRODUCTION_DEFAULTS.usersWssHasuraHost),
            usersHasuraAdminSecret: process.env.REACT_APP_USERS_HASURA_ADMIN_SECRET
        }
    },
    api_ai: {
        merchant: {
            host: resolveNonProductionHost('REACT_APP_API_AI_HOST', NON_PRODUCTION_DEFAULTS.aiHost, PRODUCTION_DEFAULTS.aiHost)
        }
    },
    AUTH_CONFIG: isDashboardLocalDebugMode() ? LOCAL_DEBUG_AUTH_CONFIG : NON_PRODUCTION_AUTH_CONFIG,
    api_payments: {
        host: resolveNonProductionHost('REACT_APP_API_PAYMENTS_HOST', NON_PRODUCTION_DEFAULTS.paymentsHost, PRODUCTION_DEFAULTS.paymentsHost),
        client_id: process.env.REACT_APP_PAYMENT_CLIENT_ID,
        client_secret: process.env.REACT_APP_PAYMENT_CLIENT_SECRET
    },
    STRIPE: {
        publicKey: resolveStripePublicKey(),
        connectedAccountID: process.env.REACT_APP_CONNECTED_STRIPE_ACCOUNT_ID
    },
    GOOGLE_MAP: {
        accessKey: process.env.REACT_APP_GOOGLE_MAP_KEY
    },
    api_files: {
        host: resolveNonProductionHost('REACT_APP_API_FILES_HOST', NON_PRODUCTION_DEFAULTS.filesHost, PRODUCTION_DEFAULTS.filesHost)
    },
    web_tracking: {
        host: resolveNonProductionHost('REACT_APP_WEB_TRACKING_HOST', NON_PRODUCTION_DEFAULTS.webTrackingHost, PRODUCTION_DEFAULTS.webTrackingHost)
    }
};

const prod = {
    environment: {
        mode: 'production',
        stage: 'production'
    },
    api_inventory: {
        admin: {
            inventoryHost: PRODUCTION_DEFAULTS.inventoryHost,
            hasuraHost: PRODUCTION_DEFAULTS.inventoryHasuraHost,
            InventoryHasuraAdminSecret: process.env.REACT_APP_INVENTORY_HASURA_ADMIN_SECRET
        }
    },
    api_shipments: {
        admin: {
            shipmentHost: PRODUCTION_DEFAULTS.shipmentHost,
            hasuraHost: PRODUCTION_DEFAULTS.shipmentHasuraHost,
            wssHasuraHost: PRODUCTION_DEFAULTS.shipmentWssHasuraHost,
            shipmentsHasuraAdminSecret: process.env.REACT_APP_SHIPMENTS_HASURA_ADMIN_SECRET
        },
        merchant: {
            shipmentHost: PRODUCTION_DEFAULTS.shipmentHost,
            hasuraHost: PRODUCTION_DEFAULTS.shipmentHasuraHost
        }
    },
    api_users: {
        admin: {
            usersHost: PRODUCTION_DEFAULTS.usersHost,
            hasuraHost: PRODUCTION_DEFAULTS.usersHasuraHost,
            hasuraRest: PRODUCTION_DEFAULTS.usersHasuraRest,
            wssHasuraHost: PRODUCTION_DEFAULTS.usersWssHasuraHost,
            usersHasuraAdminSecret: process.env.REACT_APP_USERS_HASURA_ADMIN_SECRET
        }
    },
    api_ai: {
        merchant: {
            host: PRODUCTION_DEFAULTS.aiHost
        }
    },
    AUTH_CONFIG: {
        domain: 'gophr.auth0.com',
        clientId: '08DTSFzoagUGkvFibKF8h1Bo8tuXJzBA',
        callbackUrl: 'https://dashboard.gophr.app/callback',
        container: 'auth0-login-container',
        audience: 'https://gophr.app',
        jwksUri: 'https://gophr.auth0.com/.well-known/jwks.json',
        algorithms: ['RS256'],
        issuer: 'https://gophr.auth0.com/',
        client: null,
        SIGNING_KEY: 'MzRFOUM3OTE0NzQ5NUI5NjE4OUI0MkUxMkI2RkFFNkYyRUFFNjNENw'
    },
    api_payments: {
        //! Host might change
        host: PRODUCTION_DEFAULTS.paymentsHost,
        client_id: process.env.REACT_APP_PAYMENT_CLIENT_ID,
        client_secret: process.env.REACT_APP_PAYMENT_CLIENT_SECRET
    },
    STRIPE: {
        publicKey: resolveStripePublicKey(),
        connectedAccountID: process.env.REACT_APP_CONNECTED_STRIPE_ACCOUNT_ID
    },
    GOOGLE_MAP: {
        accessKey: process.env.REACT_APP_GOOGLE_MAP_KEY
    },
    api_files: {
        host: PRODUCTION_DEFAULTS.filesHost
    },
    web_tracking: {
        host: PRODUCTION_DEFAULTS.webTrackingHost
    }
};

const config = isProductionStage() ? prod : dev;
export default {
    config
};
